/**
 * Payments page HTTP helpers only (GET /api/payments).
 * Do NOT use for billing registry "Payé" — use chargePaidAmount + PaymentAllocation.
 *
 * Confirmed list filters: sort, page, search, guestPayerName, paymentType, users, role,
 * classroom, paymentMonth, ticketNumberSearch.
 * Identity filters are mutually exclusive: users XOR guestPayerName XOR (free-text search
 * without guestPayerName). guestPayerName must never be combined with users or search.
 * startDate/endDate appear in an internal API pattern note; they are only sent
 * after probePaymentsDateRangeSupport() confirms the server applies them.
 */
import axios from 'axios'
import {fetchPaymentsUrl} from '../../redux/api'
import type {Payment} from '../../models'
import {serializePaymentsScopeKey} from '../../redux/reducers/payments/scopeCache'
import {isIsoDateInInclusiveRange, paymentReceiptDate} from '../payments/paymentAcademicYear'

export function paymentUserId(user: Payment['user'] | undefined): string | undefined {
  if (!user) return undefined
  if (typeof user === 'string') return user
  const id = user._id
  return id ? String(id) : undefined
}

/** Same query shape as Payments page (`redux/reducers/payments/service`). */
export type FetchPaymentsListParams = {
  sortField?: string
  sortOrder?: string
  pageNumber?: number
  pageCount?: number
  paymentType?: string
  role?: string
  users?: string[]
  search?: string
  guestPayerName?: string
  classroom?: string
  paymentMonth?: string
  ticketNumberSearch?: string
  startDate?: string
  endDate?: string
}

export function registeredAccountIdentity(userId: string) {
  return {search: '', users: [userId], guestPayerName: '', pageNumber: 1}
}

export function guestPayerIdentity(guestPayerName: string) {
  return {search: '', users: [] as string[], guestPayerName, pageNumber: 1}
}

export function clearPaymentIdentity() {
  return {search: '', users: [] as string[], guestPayerName: '', pageNumber: 1}
}

/** Never combine guestPayerName with users or search (backend 400 CONFLICTING_PAYMENT_IDENTITY_FILTERS). */
export function exclusiveListIdentityParams(params: Pick<FetchPaymentsListParams, 'users' | 'search' | 'guestPayerName'>): {
  users: string[]
  search: string
  guestPayerName: string
} {
  const guestPayerName = String(params.guestPayerName || '').trim()
  const users = (params.users || []).map((id) => String(id || '').trim()).filter(Boolean)
  const search = String(params.search || '').trim()
  if (guestPayerName) {
    return {guestPayerName, users: [], search: ''}
  }
  return {guestPayerName: '', users, search}
}

export function buildPaymentsListUrl(params: FetchPaymentsListParams): string {
  const sortField = params.sortField ?? 'createdAt'
  const sortOrder = params.sortOrder ?? 'desc'
  const pageNumber = params.pageNumber ?? 1
  const pageCount = params.pageCount ?? 50
  const paymentType = params.paymentType ?? 'received'
  const role = params.role ?? '2'
  const identity = exclusiveListIdentityParams(params)

  let url = `${fetchPaymentsUrl}?sortField=${sortField}&sortOrder=${sortOrder}&pageNumber=${pageNumber}&pageCount=${pageCount}&paymentType=${paymentType}&role=${role}`

  if (identity.guestPayerName) {
    url += '&guestPayerName=' + encodeURIComponent(identity.guestPayerName)
  } else {
    if (identity.search) {
      url += '&search=' + encodeURIComponent(identity.search)
    }
    if (identity.users.length) {
      url += '&users=' + identity.users.map((u) => encodeURIComponent(u)).join(',')
    }
  }
  if (params.classroom) {
    url += '&classroom=' + encodeURIComponent(params.classroom)
  }
  if (params.paymentMonth) {
    url += '&paymentMonth=' + encodeURIComponent(params.paymentMonth)
  }
  if (params.ticketNumberSearch?.trim()) {
    url += '&ticketNumberSearch=' + encodeURIComponent(params.ticketNumberSearch.trim())
  }
  if (params.startDate) {
    url += '&startDate=' + encodeURIComponent(params.startDate)
  }
  if (params.endDate) {
    url += '&endDate=' + encodeURIComponent(params.endDate)
  }
  return url
}

const SCOPE_PAGE_COUNT = 100
const SCOPE_PAGE_CONCURRENCY = 4

/** Paginate GET /api/payments until all matching receipts are loaded. */
export async function fetchAllReceivedPayments(
  params: Omit<FetchPaymentsListParams, 'pageNumber' | 'pageCount'>
): Promise<Payment[]> {
  const pageCount = SCOPE_PAGE_COUNT
  const firstUrl = buildPaymentsListUrl({
    ...params,
    paymentType: params.paymentType ?? 'received',
    pageNumber: 1,
    pageCount,
  })
  const first = await axios.get(firstUrl)
  const all: Payment[] = [...(first.data?.payments ?? [])]
  const totalPages = Number(first.data?.totalPages || 1)
  if (totalPages <= 1 || all.length < pageCount) return all

  for (let page = 2; page <= totalPages; page += SCOPE_PAGE_CONCURRENCY) {
    const chunk = []
    for (let n = page; n < page + SCOPE_PAGE_CONCURRENCY && n <= totalPages; n += 1) {
      chunk.push(
        axios.get(
          buildPaymentsListUrl({
            ...params,
            paymentType: params.paymentType ?? 'received',
            pageNumber: n,
            pageCount,
          })
        )
      )
    }
    const pages = await Promise.all(chunk)
    for (const res of pages) {
      all.push(...(res.data?.payments ?? []))
    }
  }

  return all
}

/**
 * Detect whether GET /api/payments honors startDate/endDate.
 * A range with no real receipts must return empty if the filter is applied.
 * If the server ignores the params, it returns the latest receipt instead.
 */
export async function probePaymentsDateRangeSupport(
  params: Pick<FetchPaymentsListParams, 'role' | 'users'>
): Promise<boolean> {
  try {
    const url = buildPaymentsListUrl({
      sortField: 'createdAt',
      sortOrder: 'desc',
      pageNumber: 1,
      pageCount: 1,
      paymentType: 'received',
      role: params.role || '2',
      users: params.users,
      startDate: '2000-01-01',
      endDate: '2000-01-02',
    })
    const {data} = await axios.get(url)
    const payments: Payment[] = data?.payments ?? []
    const total = Number(data?.total || 0)
    if (payments.length === 0 && total === 0) return true
    return payments.every((payment) =>
      isIsoDateInInclusiveRange(paymentReceiptDate(payment), '2000-01-01', '2000-01-02')
    )
  } catch {
    return false
  }
}

export function paymentsRespectDateRange(
  payments: Payment[],
  startDate: string,
  endDate: string
): boolean {
  return payments.every((payment) => {
    const date = paymentReceiptDate(payment)
    if (!date) return true
    return isIsoDateInInclusiveRange(date, startDate, endDate)
  })
}

const inFlightScopeFetches = new Map<string, Promise<Payment[]>>()

/** Same as fetchAllReceivedPayments, but reuses an identical in-flight request. */
export function fetchAllReceivedPaymentsShared(
  params: Omit<FetchPaymentsListParams, 'pageNumber' | 'pageCount'>
): Promise<Payment[]> {
  const key = serializePaymentsScopeKey(params)
  const existing = inFlightScopeFetches.get(key)
  if (existing) return existing

  const request = fetchAllReceivedPayments(params).finally(() => {
    inFlightScopeFetches.delete(key)
  })
  inFlightScopeFetches.set(key, request)
  return request
}

/** Receipts from GET /api/payments — for Payments UI / debug only, not billing Payé. */
export async function fetchPaymentsForUsers(
  userIds: string[],
  options?: Omit<FetchPaymentsListParams, 'users'>
): Promise<Payment[]> {
  const ids = [...new Set(userIds.map((id) => id.trim()).filter(Boolean))]
  if (!ids.length) return []

  const url = buildPaymentsListUrl({
    sortField: 'createdAt',
    sortOrder: 'desc',
    pageNumber: 1,
    pageCount: 999999,
    paymentType: 'received',
    role: '2',
    users: ids,
    ...options,
  })

  const {data} = await axios.get(url)
  const list: Payment[] = data?.payments ?? []
  const idSet = new Set(ids)
  return list.filter((pay) => {
    const uid = paymentUserId(pay.user)
    return uid ? idSet.has(uid) : false
  })
}
